import { ChangeEvent, FC, useState } from 'react'
import {
  useClientApiCall,
  useClientApiMutation,
} from '../../hooks/useClientApiCall'
import { useSearchParams } from 'react-router-dom'
import { ApplicationLoadingScreen } from '../../components/ApplicationLoadingScreen'
import { AppplicationErrorScreen } from '../../components/ApplicationErrorScreen'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  Card,
  Box,
  CardMedia,
  Grid,
  Button,
  Container,
  MenuItem,
  CardContent,
  Select,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Tooltip,
  IconButton,
  Divider,
  DialogContent,
  ListItemButton,
  DialogActions,
  ListItem,
  List,
  Dialog,
  DialogTitle,
  ListItemText,
  FormControl,
  InputLabel,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AntSwitch } from './utils/utils'
import { toast } from 'react-toastify'
import React from 'react'

/**
 * Uber Eats Application
 */
export const UberEats: FC = () => {
  const [params] = useSearchParams()
  const status = useClientApiCall<any>(params.get('session')!, 'status')

  if (status.isLoading) return <ApplicationLoadingScreen />
  if (status.isError) return <AppplicationErrorScreen />

  return <UberEatsView status={status} />
}

export const UberEatsView: FC<{ status: any }> = ({ status }) => {
  const [params] = useSearchParams()
  const [selectedStoreId, setSelectedStoreId] = useState<string | undefined>()

  const [isPriceHikeEnabled, setPriceHikeToggle] = useState(
    status?.data?.connection.isPriceHike || false
  )
  const [hikePercentage, setPriceHikePercentage] = useState(
    status?.data?.connection.priceHikePercent || 20
  )

  const [dialogOpen, setDialogOpen] = useState(false)

  const [selectedMenuId, setSelectedMenuId] = useState('')

  const [isRoundOffEnabled, setRoundOffEnabled] = useState(
    status?.data?.connection.roundOff !== false
  )

  const [isOrdersOnly, setIsOrdersOnly] = useState(
    status?.data?.connection.ordersOnlyIntegration || false
  )

  // Pause/Resume state management - derived from Uber API response
  const uberStoreStatus = status?.data?.uberStoreStatus
  const isStoreOffline = uberStoreStatus?.status === 'OFFLINE'

  const [pauseDuration, setPauseDuration] = useState<
    '15' | '30' | '60' | 'custom'
  >('15')
  const [customPauseDateTime, setCustomPauseDateTime] = useState<Date | null>(
    null
  )
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false)
  const [pauseReason, setPauseReason] = useState('')

  const isConnected = status.data?.connection?.externalLocationId
  const token = status.data?.connection?.uberAccessToken
  const menuList = status.data?.menuList || []
  const connectMutation = useClientApiMutation<any>(
    params.get('session')!,
    'connect'
  )
  const menuSyncMutation = useClientApiMutation<any>(
    params.get('session')!,
    'publish-menu',
    'PUT'
  )
  const disconnectMutation = useClientApiMutation<any>(
    params.get('session')!,
    'connect',
    'DELETE'
  )

  const loginMutation = useClientApiMutation<any>(
    params.get('session')!,
    'callback',
    'GET'
  )

  const updateConnectionMutation = useClientApiMutation<any>(
    params.get('session')!,
    'status',
    'PUT'
  )

  const pauseStoreMutation = useClientApiMutation<any>(
    params.get('session')!,
    'pause-store',
    'POST'
  )

  const resumeStoreMutation = useClientApiMutation<any>(
    params.get('session')!,
    'resume-store',
    'POST'
  )

  const handleConnect = () => {
    if (selectedStoreId) {
      connectMutation.mutate(
        { storeId: selectedStoreId },
        {
          onSuccess: () => {
            status.refetch()
          },
        }
      )
    }
  }

  const handleDisconnect = () => {
    const storeId = status.data?.connection?.externalLocationId
    if (storeId) {
      disconnectMutation.mutate(
        { storeId },
        {
          onSuccess: () => {
            status.refetch()
            window.close()
          },
        }
      )
    }
  }

  const handleLogin = async () => {
    loginMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (data?.headers?.Location) {
          window.location.href = data.headers.Location
        }
      },
    })
  }

  const handlePriceHikeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceHikeToggle(event.target.checked)
    event.target.checked
      ? setPriceHikePercentage(status?.data?.connection.priceHikePercent || 20)
      : setPriceHikePercentage(
          !status?.data?.connection.priceHikePercent ||
            status?.data?.connection.priceHikePercent === 0.0001
            ? 20
            : status?.data?.connection.priceHikePercent
        )

    updateConnectionMutation.mutate(
      {
        isPriceHike: event.target.checked,
        priceHikePercent: hikePercentage,
      },
      {
        onSuccess: () => {
          status.refetch()
        },
      }
    )
  }

  const handlerIsRoundOffEnabled = (event: ChangeEvent<HTMLInputElement>) => {
    setRoundOffEnabled(event.currentTarget.checked)

    updateConnectionMutation.mutate(
      { roundOff: event.currentTarget.checked },
      {
        onSuccess: () => {
          status.refetch()
        },
      }
    )
  }

  const handlerIsOrdersOnlyEnabled = (event: ChangeEvent<HTMLInputElement>) => {
    setIsOrdersOnly(event.currentTarget.checked)

    updateConnectionMutation.mutate(
      { ordersOnlyIntegration: event.currentTarget.checked },
      {
        onSuccess: () => {
          status.refetch()
        },
      }
    )
  }
  const handlePriceHikeUpdate = () => {
    if (hikePercentage > 0) {
      updateConnectionMutation.mutate(
        { priceHikePercent: +hikePercentage },
        {
          onSuccess: () => {
            toast.success('Price hike percentage updated successfully!')
            status.refetch()
          },
        }
      )
    } else {
      toast.error('Price hike percentage must be greater than zero.')
    }
  }

  const handleSwitchAccount = () => {
    updateConnectionMutation.mutate(
      { switchAccount: true },
      {
        onSuccess: () => {
          status.refetch()
        },
      }
    )
  }
  const handleHikePercentage = (event: ChangeEvent<HTMLInputElement>) => {
    const hikePercent = event.target.value

    // Allow empty input or valid decimal up to 2 places
    if (/^\d*\.?\d{0,2}$/.test(hikePercent) && !/[a-zA-Z]/.test(hikePercent)) {
      const numericValue = parseFloat(hikePercent)

      // Allow if empty (for clearing input) or greater than 0
      if (hikePercent === '' || numericValue > 0) {
        setPriceHikePercentage(hikePercent)
      }
    }
  }

  const handleDialogOpen = () => {
    setDialogOpen(true)
  }

  const handleMenuPublish = (menuId: string) => {
    setDialogOpen(false)
    setSelectedMenuId(menuId)

    menuSyncMutation.mutate(
      { menuId },
      {
        onSuccess: () => {
          toast.success('Menu Sync Initiated!')
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message || 'Failed to publish menu.'
          )
        },
      }
    )
  }

  const connectedStore = status.data?.stores?.find(
    (s: any) => s.store_id === isConnected
  )

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  // Pause/Resume handlers
  const handlePauseStore = () => {
    setPauseDialogOpen(true)
  }

  const handleResumeStore = () => {
    resumeStoreMutation.mutate(
      {
        reason: 'Back to business',
      },
      {
        onSuccess: (data) => {
          toast.success('Store resumed successfully!')
          status.refetch()
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message || 'Failed to resume store.'
          )
        },
      }
    )
  }

  const handlePauseConfirm = () => {
    let endTime = ''

    if (pauseDuration === 'custom') {
      if (!customPauseDateTime) {
        toast.error('Please select a custom pause end time.')
        return
      }

      // Check if the selected time is in the future
      const now = new Date()
      if (customPauseDateTime <= now) {
        toast.error('Pause end time must be in the future.')
        return
      }

      endTime = customPauseDateTime.toISOString()
    } else {
      // Calculate end time for predefined durations
      const pauseMinutes = parseInt(pauseDuration)
      const now = new Date()
      const endDateTime = new Date(now.getTime() + pauseMinutes * 60000)
      endTime = endDateTime.toISOString()
    }

    pauseStoreMutation.mutate(
      {
        endTime: endTime,
        reason: pauseReason || undefined,
      },
      {
        onSuccess: (data) => {
          setPauseDialogOpen(false)
          setPauseReason('')
          setCustomPauseDateTime(null)
          setPauseDuration('15')

          const durationText =
            pauseDuration === 'custom'
              ? `until ${customPauseDateTime?.toLocaleString()}`
              : `${pauseDuration} minutes`
          toast.success(`Store paused ${durationText}!`)
          status.refetch() // This will fetch fresh Uber status
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message || 'Failed to pause store.'
          )
        },
      }
    )
  }

  const handlePauseDialogClose = () => {
    setPauseDialogOpen(false)
    setCustomPauseDateTime(null)
    setPauseDuration('15')
    setPauseReason('')
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ height: '100vh' }}>
      <Card sx={{ display: 'flex', height: '100%' }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Shared Left Side: UberEats Logo */}
          <Grid
            item
            xs={12}
            sm={8}
            sx={{
              display: { xs: 'none', sm: 'flex' }, // 👈 Hides on mobile
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src="/assets/logos/poshub_uber_banner.jpg"
              alt="UberEats Logo"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                display: 'block',
              }}
            />
          </Grid>

          {/* Right Side: Conditional - Connected or Not Connected */}
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              p: 4,
              textAlign: 'center',
            }}
          >
            {isConnected && connectedStore ? (
              // ✅ Connected View
              <>
                <CardContent sx={{ width: '100%', maxWidth: 400 }}>
                  <Box display="flex" justifyContent="center" mb={2}>
                    <CardMedia
                      component="img"
                      src={connectedStore.raw_hero_url}
                      alt="Store Logo"
                      sx={{
                        height: 150,
                        width: '100%',
                        objectFit: 'cover',
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={510}
                      fontFamily="'Inter', 'Roboto', sans-serif"
                    >
                      {connectedStore.name}
                    </Typography>
                    {/* Live Status Indicator */}
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: isStoreOffline ? '#f44336' : '#4caf50',
                        boxShadow: `0 0 0 2px ${isStoreOffline ? '#ffebee' : '#e8f5e8'}`,
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: `0 0 0 0 ${isStoreOffline ? 'rgba(244, 67, 54, 0.4)' : 'rgba(76, 175, 80, 0.4)'}`,
                          },
                          '70%': {
                            boxShadow: `0 0 0 6px ${isStoreOffline ? 'rgba(244, 67, 54, 0)' : 'rgba(76, 175, 80, 0)'}`,
                          },
                          '100%': {
                            boxShadow: `0 0 0 0 ${isStoreOffline ? 'rgba(244, 67, 54, 0)' : 'rgba(76, 175, 80, 0)'}`,
                          },
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={510}
                      fontFamily="'Inter', 'Roboto', sans-serif"
                      color={isStoreOffline ? 'error.main' : 'success.main'}
                    >
                      {isStoreOffline ? 'Paused' : 'Active'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontFamily="'Inter', 'Roboto', sans-serif"
                    fontWeight={400}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <LocationOnIcon fontSize="small" color="action" />
                    {connectedStore.location?.address || 'N/A'}
                  </Typography>
                </CardContent>

                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    mt: 2,
                    width: { xs: '100%', sm: '75%', md: '75%' },
                  }}
                >
                  <Stack spacing={2}>
                    {/* Enable Price Hike Row */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="body1"
                        fontWeight={510}
                        fontFamily="'Inter', 'Roboto', sans-serif"
                      >
                        Enable Price Hike
                      </Typography>
                      <AntSwitch
                        checked={isPriceHikeEnabled}
                        onChange={handlePriceHikeChange}
                        color="success"
                      />
                    </Stack>

                    {/* Price Hike Input */}
                    {isPriceHikeEnabled && (
                      <TextField
                        label="Price Hike Percentage"
                        variant="standard"
                        size="small"
                        required
                        sx={{ maxWidth: 200 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        helperText="Value must be greater than 0"
                        FormHelperTextProps={{ sx: { mt: 0.5, fontSize: 12 } }}
                        value={hikePercentage}
                        onChange={handleHikePercentage}
                        onBlur={handlePriceHikeUpdate}
                      />
                    )}
                    <Divider sx={{ my: 1 }} />

                    {/* Round Off Prices */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                          arrow
                          enterTouchDelay={0}
                          leaveTouchDelay={3000}
                          title={
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-line' }}
                            >
                              <strong>Enable to Round Total:</strong>
                              {'\n'}This option rounds off the total value on
                              the order receipt & website
                              {'\n\n'}Eg:{'\n'}6.77 → 6.8{'\n'}6.33 → 6.3
                            </Typography>
                          }
                        >
                          <IconButton size="small" sx={{ color: 'black' }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="body1"
                          fontWeight={510}
                          fontFamily="'Inter', 'Roboto', sans-serif"
                        >
                          Round off price
                        </Typography>
                      </Stack>
                      <AntSwitch
                        checked={isRoundOffEnabled}
                        onChange={handlerIsRoundOffEnabled}
                        color="success"
                      />
                    </Stack>
                    <Divider sx={{ my: 1 }} />

                    {/* Orders-Only Integration */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                          arrow
                          enterTouchDelay={0}
                          leaveTouchDelay={3000}
                          title={
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-line' }}
                            >
                              When enabled, this mode allows Foodhub to receive
                              orders from Uber without sending back any updates.
                              {'\n'}- No menu
                              {'\n'}- No order status updates
                              {'\n\n'}
                              Use this when you want to receive Uber orders in
                              Foodhub, but manage everything else directly on
                              Uber.
                            </Typography>
                          }
                        >
                          <IconButton size="small" sx={{ color: 'black' }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="body1"
                          fontWeight={510}
                          fontFamily="'Inter', 'Roboto', sans-serif"
                        >
                          Orders-Only Integration
                        </Typography>
                      </Stack>
                      <AntSwitch
                        checked={isOrdersOnly}
                        onChange={handlerIsOrdersOnlyEnabled}
                        color="success"
                      />
                    </Stack>
                    <Divider sx={{ my: 1 }} />

                    {/* Pause/Resume Store Section */}
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1}>
                        {isStoreOffline ? (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<PlayArrowIcon />}
                            onClick={handleResumeStore}
                            disabled={resumeStoreMutation.isLoading}
                            sx={{
                              flex: 1,
                              fontFamily: "'Inter', 'Roboto', sans-serif",
                              fontWeight: 520,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                            }}
                          >
                            {resumeStoreMutation.isLoading
                              ? 'Resuming...'
                              : 'Resume Store'}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<PauseIcon />}
                            onClick={handlePauseStore}
                            sx={{
                              flex: 1,
                              fontFamily: "'Inter', 'Roboto', sans-serif",
                              fontWeight: 520,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                            }}
                          >
                            Pause Store
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    {!isOrdersOnly && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleDialogOpen}
                          sx={{
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            fontWeight: 520,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                          }}
                        >
                          Publish Menu
                        </Button>

                        <Dialog
                          open={dialogOpen}
                          onClose={handleDialogClose}
                          fullWidth
                          maxWidth="sm"
                          PaperProps={{
                            sx: {
                              position: 'absolute',
                              top: '50%',
                              right: { xs: '50%', md: 32 },
                              transform: {
                                xs: 'translate(50%, -50%)',
                                md: 'translate(0, -50%)',
                              },
                              width: { xs: '90%', sm: '80%', md: 400 },
                              margin: 0,
                            },
                          }}
                        >
                          <DialogTitle>Select a Menu to Publish</DialogTitle>
                          <DialogContent>
                            <List>
                              {menuList.map((menu: any, index: number) => (
                                <React.Fragment key={menu.id}>
                                  <ListItem disablePadding>
                                    <ListItemButton
                                      onClick={() => handleMenuPublish(menu.id)}
                                    >
                                      <ListItemText primary={menu.name} />
                                    </ListItemButton>
                                  </ListItem>
                                  {/* Only add divider if xit's not the last item */}
                                  {index < menuList.length - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                            </List>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={handleDialogClose}>Cancel</Button>
                          </DialogActions>
                        </Dialog>
                      </>
                    )}

                    {/* Pause Duration Selection Dialog */}
                    <Dialog
                      open={pauseDialogOpen}
                      onClose={handlePauseDialogClose}
                      fullWidth
                      maxWidth="sm"
                      PaperProps={{
                        sx: {
                          position: 'absolute',
                          top: '50%',
                          right: { xs: '50%', md: 32 },
                          transform: {
                            xs: 'translate(50%, -50%)',
                            md: 'translate(0, -50%)',
                          },
                          width: { xs: '90%', sm: '80%', md: 400 },
                          margin: 0,
                        },
                      }}
                    >
                      <DialogTitle>Pause Store</DialogTitle>
                      <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Select how long you want to pause your store:
                          </Typography>

                          <FormControl fullWidth>
                            <InputLabel>Pause Duration</InputLabel>
                            <Select
                              value={pauseDuration}
                              onChange={(e) =>
                                setPauseDuration(e.target.value as any)
                              }
                              label="Pause Duration"
                            >
                              <MenuItem value="15">15 minutes</MenuItem>
                              <MenuItem value="30">30 minutes</MenuItem>
                              <MenuItem value="60">1 hour</MenuItem>
                              <MenuItem value="custom">
                                Custom duration
                              </MenuItem>
                            </Select>
                          </FormControl>

                          {pauseDuration === 'custom' && (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DateTimePicker
                                label="Pause Until"
                                value={customPauseDateTime}
                                onChange={(newValue) =>
                                  setCustomPauseDateTime(newValue)
                                }
                                minDateTime={new Date()}
                                slotProps={{
                                  textField: {
                                    helperText:
                                      'Select when the pause should end',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          )}

                          <TextField
                            label="Reason for pausing (optional)"
                            value={pauseReason}
                            onChange={(e) => setPauseReason(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            helperText="Provide a reason for pausing the store"
                            placeholder="e.g., Store maintenance, equipment repair, staff break..."
                          />

                          {pauseDuration !== 'custom' ? (
                            <Typography variant="body2" color="text.secondary">
                              Your store will be paused for {pauseDuration}{' '}
                              minutes and will automatically resume.
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Your store will be paused until the selected date
                              and time.
                            </Typography>
                          )}
                        </Stack>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handlePauseDialogClose}>Cancel</Button>
                        <Button
                          onClick={handlePauseConfirm}
                          variant="contained"
                          color="warning"
                          disabled={pauseStoreMutation.isLoading}
                        >
                          {pauseStoreMutation.isLoading
                            ? 'Pausing...'
                            : 'Pause Store'}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Stack>
                </Box>

                <Box display="flex" gap={2} mt={2}>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() =>
                      window.open(
                        connectedStore.web_url,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    sx={{
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                      fontWeight: 520,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                    }}
                  >
                    View Website
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDisconnect}
                    sx={{
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                      fontWeight: 520,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                    }}
                  >
                    Disconnect
                  </Button>
                </Box>
              </>
            ) : !isConnected && !token ? (
              // 🚫 Connected but no store info
              <Box width="100%" maxWidth={400} textAlign="center">
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Please log in to complete the connection and view store
                  details.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleLogin}
                  disabled={loginMutation.isLoading}
                >
                  Login to Uber
                </Button>
              </Box>
            ) : token ? (
              // 🔌 Not Connected View (only if token exists)
              <Box width="100%" maxWidth={400}>
                <Typography variant="h6" gutterBottom>
                  Select a store to connect
                </Typography>

                <Select<string>
                  fullWidth
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                  displayEmpty
                  sx={{ mb: 2 }}
                >
                  <MenuItem disabled value="">
                    Select store
                  </MenuItem>
                  {status.data?.stores?.map((store: any) => (
                    <MenuItem key={store.store_id} value={store.store_id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>

                <Box display="flex" flexDirection="column" gap={2}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleConnect}
                    disabled={!selectedStoreId || connectMutation.isLoading}
                  >
                    {connectMutation.isLoading ? 'Connecting...' : 'Connect'}
                  </Button>

                  <Button
                    variant="outlined"
                    color="success"
                    onClick={handleSwitchAccount}
                  >
                    Switch Account
                  </Button>
                </Box>
              </Box>
            ) : (
              // 💤 Fallback or empty state
              <Card
                sx={{
                  p: 3,
                  boxShadow: 2,
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: '#fafafa',
                }}
              >
                <InfoOutlinedIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Unable to fetch store information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please try refreshing the page or check your connection.
                </Typography>
              </Card>
            )}
          </Grid>
        </Grid>
      </Card>
    </Container>
  )
}
